import { useEffect } from "react";

// Several overlays can be open at once (for example a confirmation on top of
// an edit dialog). Restore each scroll container only after the last closes.
const activeLocks = new Map();

function lockScroll(element) {
  const current = activeLocks.get(element);
  if (current) {
    current.count += 1;
  } else {
    const scrollTop = element.scrollTop;
    const scrollLeft = element.scrollLeft;
    activeLocks.set(element, {
      count: 1,
      overflowY: element.style.overflowY,
      overscrollBehaviorY: element.style.overscrollBehaviorY,
      scrollTop,
      scrollLeft,
    });
    element.style.overflowY = "hidden";
    element.style.overscrollBehaviorY = "none";
    // Changing the root overflow can reset document scrolling in Chromium.
    // Keep the underlying page at its position while the overlay is open.
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  }

  return () => {
    const lock = activeLocks.get(element);
    if (!lock || --lock.count > 0) return;
    element.style.overflowY = lock.overflowY;
    element.style.overscrollBehaviorY = lock.overscrollBehaviorY;
    element.scrollTop = lock.scrollTop;
    element.scrollLeft = lock.scrollLeft;
    activeLocks.delete(element);
  };
}

export default function useOverlayScrollLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;

    // Desktop scrolls the document; mobile/tablet Layout scrolls <main>
    // instead. Lock both so a wheel over the scrim cannot move the page.
    const releaseDocument = [document.documentElement, document.body].map(lockScroll);
    let lockedMain = null;
    let releaseMain = null;
    const syncMain = () => {
      const main = document.querySelector('[data-testid="main-content"]');
      // A previously locked main has computed overflow:hidden. Other overlays
      // must still acquire a reference so closing the first one cannot unlock it.
      const next = main && (activeLocks.has(main) || /^(auto|scroll)$/.test(getComputedStyle(main).overflowY))
        ? main : null;
      if (next === lockedMain) return;
      releaseMain?.();
      lockedMain = next;
      releaseMain = next ? lockScroll(next) : null;
    };
    syncMain();

    // Layout replaces <main> when switching between desktop and mobile.
    // Transfer the lock to the replacement without requiring the overlay to
    // close and reopen. Child-list only avoids reacting to our style writes.
    const observer = new MutationObserver(syncMain);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", syncMain);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncMain);
      releaseMain?.();
      releaseDocument.reverse().forEach(release => release());
    };
  }, [active]);
}